/**
 * The bar WINE LIST — wine sold by the glass and by the bottle, generated from
 * the wines a restaurant carries (Menu → Bar → Wine list). Dark behind
 * `initiative.bar_wine_list`. SSOT: `docs/BAR_CATALOG_ARCHITECTURE.md`
 * → *Wine list*; design `docs/superpowers/specs/2026-10-09-bar-wine-list-design.md`.
 *
 * Pure rules only. The service (`packages/core/src/services/bar/barWineListService.ts`)
 * reads and writes; everything it decides comes from here, so the management
 * page and the API cannot disagree about a pour, a label or a document id.
 *
 * Shape, per listed wine (all at DETERMINISTIC ids, created atomically —
 * `FIRESTORE_DOCUMENT_IDS.md` Rule 3):
 *   - `items/bar_wine_{inventoryGuid}` — the menu item, created INACTIVE at
 *     `basePrice: 0` (the serving carries the price), like every generated bar
 *     item;
 *   - `modifier_groups/bar_wine_{inventoryGuid}` — its REQUIRED "Serving"
 *     group (exactly one);
 *   - `modifiers/bar_wine_{inventoryGuid}_{serving}` — Glass / Large Glass /
 *     Bottle, each with its own price, each created OFF and held until priced;
 *   - `modifier_ingredients/{option guid}` — the serving's DRAW on the wine's
 *     own inventory item: a glass takes the glass pour, a bottle the whole
 *     bottle. Stock comes off at order close like every modifier draw.
 */
import { reorderOwnSlots } from "../menus/reorderOwnSlots";
import { formatBarBottleSize, ML_PER_STOCK_UNIT } from "./BarBottle";

// ── Wine styles ──────────────────────────────────────────────────────────

/** How a wine list is grouped and how long an open bottle keeps. */
export const BAR_WINE_STYLES = ["sparkling", "white", "rose", "red", "dessert", "other"] as const;
export type BarWineStyle = (typeof BAR_WINE_STYLES)[number];

export const BAR_WINE_STYLE_LABELS: Readonly<Record<BarWineStyle, string>> = {
  sparkling: "Sparkling",
  white: "White",
  rose: "Rosé",
  red: "Red",
  dessert: "Dessert & Fortified",
  other: "Other",
};

/**
 * Words that name a style, most specific first: "Sparkling Rosé" is sparkling
 * (it is served and keeps like sparkling), "White Zinfandel" is a rosé, and
 * "Port" is dessert before any grape word can claim it. Matched on whole words
 * against the catalog's subcategory, then the product name.
 */
const STYLE_WORDS: ReadonlyArray<readonly [BarWineStyle, readonly string[]]> = [
  [
    "sparkling",
    ["sparkling", "champagne", "veuve", "blanc de blancs", "prosecco", "cava", "cremant", "crémant", "spumante", "brut", "lambrusco", "moscato d'asti", "asti", "sekt", "franciacorta", "petillant", "pétillant"],
  ],
  [
    "dessert",
    ["port", "porto", "sherry", "madeira", "marsala", "sauternes", "tokaji", "ice wine", "icewine", "late harvest", "dessert", "fortified", "vin santo", "muscat de"],
  ],
  ["rose", ["rose", "rosé", "rosado", "rosato", "blush", "white zinfandel", "white zin"]],
  [
    "white",
    ["white", "chardonnay", "sauvignon blanc", "pinot grigio", "pinot gris", "riesling", "moscato", "gewurztraminer", "gewürztraminer", "viognier", "chenin blanc", "albarino", "albariño", "gruner", "grüner", "vermentino", "torrontes", "torrontés", "semillon", "sémillon", "muscadet", "chablis", "sancerre", "soave", "verdejo", "fiano", "assyrtiko", "trebbiano", "garganega", "vinho verde", "blanc"],
  ],
  [
    "red",
    ["red", "cabernet", "merlot", "pinot noir", "malbec", "syrah", "shiraz", "zinfandel", "sangiovese", "tempranillo", "grenache", "garnacha", "nebbiolo", "barolo", "barbaresco", "chianti", "rioja", "bordeaux", "beaujolais", "gamay", "carmenere", "carménère", "petite sirah", "petit verdot", "mourvedre", "mourvèdre", "primitivo", "montepulciano", "barbera", "dolcetto", "aglianico", "nero d'avola", "pinotage", "tannat", "valpolicella", "amarone", "cotes du rhone", "côtes du rhône", "chateauneuf", "châteauneuf", "rouge", "tinto", "rosso", "claret", "meritage", "burgundy", "bourgogne", "super tuscan", "brunello", "crianza", "pauillac", "blaye", "medoc", "pomerol"],
  ],
];

const hasWord = (haystack: string, word: string): boolean => {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\p{L}])${escaped}($|[^\\p{L}])`, "u").test(haystack);
};

/**
 * The style a wine is listed under. The catalog's own grouping (the state
 * list's "Pinot Noir", "Sparkling Wine") decides first; the product name is
 * the fallback. A wine nothing names is `other` — never guessed into red.
 */
export function barWineStyleFor(text: {
  readonly subcategory?: string | null;
  readonly name?: string | null;
}): BarWineStyle {
  for (const source of [text.subcategory, text.name]) {
    const t = (source ?? "").toLowerCase();
    if (!t.trim()) continue;
    for (const [style, words] of STYLE_WORDS) {
      if (words.some((w) => hasWord(t, w))) return style;
    }
  }
  return "other";
}

// ── Servings ─────────────────────────────────────────────────────────────

/** What a guest orders a wine as. `large` exists only while the location offers a large pour. */
export const BAR_WINE_SERVINGS = ["glass", "large", "bottle"] as const;
export type BarWineServing = (typeof BAR_WINE_SERVINGS)[number];

/**
 * The option names. Deliberately WITHOUT the pour size: the size is a location
 * setting that can change, and a name that embedded it would have to be
 * rewritten on every option a restaurant may have renamed. The size is on the
 * till as the option's POS description instead.
 */
export const BAR_WINE_SERVING_LABELS: Readonly<Record<BarWineServing, string>> = {
  glass: "Glass",
  large: "Large Glass",
  bottle: "Bottle",
};

/** The common restaurant pours, offered as presets on the page (any value in range is accepted). */
export const BAR_WINE_POUR_PRESETS_OZ: readonly number[] = [4, 5, 6, 8.5] as const;
export const BAR_WINE_GLASS_OZ_DEFAULT = 5;
/** A pour outside (0, 16] oz is refused: a glass larger than half a bottle is a typo. */
export const BAR_WINE_POUR_OZ_MAX = 16;

export interface IBarWineSettings {
  /** The by-the-glass pour, in ounces. */
  readonly glassOz: number;
  /** The large pour, in ounces; `null` = no large pour offered. */
  readonly largePourOz: number | null;
  /**
   * Open-bottle tracking (Layer 2): the bar ticket says when to open a new
   * bottle, when a glass is the last one, and when the open bottle is past its
   * shelf life. OFF by default — advisory only, never blocks a send.
   */
  readonly openBottleTracking: boolean;
  /** Days an open bottle keeps, per style; an absent style keeps its default. */
  readonly shelfLifeDays: Readonly<Record<BarWineStyle, number>>;
  /**
   * Days an open bottle of ONE wine keeps, by its inventory guid — for a wine
   * kept under a preservation system (Coravin, argon, an Enomatic) that lasts
   * far longer than its style. Outranks the style's days.
   */
  readonly shelfLifeDaysByWine: Readonly<Record<string, number>>;
}

/** How long an open bottle keeps, by style — the common bar guidance; each location can change it. */
export const BAR_WINE_SHELF_LIFE_DAYS_DEFAULT: Readonly<Record<BarWineStyle, number>> = {
  sparkling: 2,
  white: 5,
  rose: 5,
  red: 5,
  dessert: 28,
  other: 5,
};
export const BAR_WINE_SHELF_LIFE_DAYS_MAX = 90;

export const isValidBarWineShelfLifeDays = (d: unknown): d is number =>
  typeof d === "number" && Number.isInteger(d) && d >= 1 && d <= BAR_WINE_SHELF_LIFE_DAYS_MAX;

/** At most this many wines carry their own shelf life — far more than any wine list, so the block stays small. */
export const BAR_WINE_SHELF_LIFE_BY_WINE_MAX = 500;

export const LOCATION_BAR_WINE_LIST_KEYS = [
  "glassOz",
  "largePourOz",
  "openBottleTracking",
  "shelfLifeDays",
  "shelfLifeDaysByWine",
] as const;
export type LocationBarWineListKey = (typeof LOCATION_BAR_WINE_LIST_KEYS)[number];

/**
 * `restaurant.settings.barWineList` — SPARSE (an absent key is its default),
 * SERVER-WRITTEN only (`BarWineListService.saveSettings`; firestore rules
 * `serverOnlySettingsBlocks`). It decides what a serving draws from stock.
 */
export interface LocationBarWineListBlock {
  readonly glassOz?: number;
  /** Absent = no large pour; the stored block never holds `null`. */
  readonly largePourOz?: number;
  /** Only `true` is stored — off is the absent key. */
  readonly openBottleTracking?: true;
  /** Only the styles that differ from `BAR_WINE_SHELF_LIFE_DAYS_DEFAULT`. */
  readonly shelfLifeDays?: Readonly<Partial<Record<BarWineStyle, number>>>;
  /** Per wine (inventory guid); absent = every wine keeps its style's days. */
  readonly shelfLifeDaysByWine?: Readonly<Record<string, number>>;
}

export function defaultBarWineSettings(): IBarWineSettings {
  return {
    glassOz: BAR_WINE_GLASS_OZ_DEFAULT,
    largePourOz: null,
    openBottleTracking: false,
    shelfLifeDays: { ...BAR_WINE_SHELF_LIFE_DAYS_DEFAULT },
    shelfLifeDaysByWine: {},
  };
}

function shelfLifeFromJSON(raw: unknown): Partial<Record<BarWineStyle, number>> {
  const out: Partial<Record<BarWineStyle, number>> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const style of BAR_WINE_STYLES) {
    const d = (raw as Record<string, unknown>)[style];
    if (isValidBarWineShelfLifeDays(d)) out[style] = d;
  }
  return out;
}

function shelfLifeByWineFromJSON(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [guid, d] of Object.entries(raw as Record<string, unknown>)) {
    if (Object.keys(out).length >= BAR_WINE_SHELF_LIFE_BY_WINE_MAX) break;
    if (guid.length > 0 && isValidBarWineShelfLifeDays(d)) out[guid] = d;
  }
  return out;
}

/** Days an open bottle of this wine keeps: its own days if it has any, else its style's. */
export function barWineShelfLifeDaysFor(
  settings: Pick<IBarWineSettings, "shelfLifeDays" | "shelfLifeDaysByWine">,
  inventoryGuid: string,
  style: BarWineStyle,
): number {
  return (
    settings.shelfLifeDaysByWine[inventoryGuid] ??
    settings.shelfLifeDays[style] ??
    BAR_WINE_SHELF_LIFE_DAYS_DEFAULT[style]
  );
}

/** A pour the settings accept: (0, 16] oz, to the hundredth. */
export function isValidBarWinePourOz(oz: unknown): oz is number {
  return typeof oz === "number" && Number.isFinite(oz) && oz > 0 && oz <= BAR_WINE_POUR_OZ_MAX && Math.abs(oz * 100 - Math.round(oz * 100)) < 1e-9;
}

/** Resolved settings from a stored block: malformed values read as their default. */
export function barWineSettingsFromJSON(raw: Readonly<Record<string, unknown>> | null | undefined): IBarWineSettings {
  const d = defaultBarWineSettings();
  if (!raw || typeof raw !== "object") return d;
  return {
    glassOz: isValidBarWinePourOz(raw.glassOz) ? raw.glassOz : d.glassOz,
    largePourOz: isValidBarWinePourOz(raw.largePourOz) ? raw.largePourOz : null,
    openBottleTracking: raw.openBottleTracking === true,
    shelfLifeDays: { ...d.shelfLifeDays, ...shelfLifeFromJSON(raw.shelfLifeDays) },
    shelfLifeDaysByWine: shelfLifeByWineFromJSON(raw.shelfLifeDaysByWine),
  };
}

/** The stored block as the Location model hydrates it: only valid, non-default keys. */
export function barWineListBlockFromJSON(raw: unknown): LocationBarWineListBlock | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const out: {
    glassOz?: number;
    largePourOz?: number;
    openBottleTracking?: true;
    shelfLifeDays?: Partial<Record<BarWineStyle, number>>;
    shelfLifeDaysByWine?: Record<string, number>;
  } = {};
  if (isValidBarWinePourOz(r.glassOz)) out.glassOz = r.glassOz;
  if (isValidBarWinePourOz(r.largePourOz)) out.largePourOz = r.largePourOz;
  if (r.openBottleTracking === true) out.openBottleTracking = true;
  const shelf = shelfLifeFromJSON(r.shelfLifeDays);
  if (Object.keys(shelf).length > 0) out.shelfLifeDays = shelf;
  const byWine = shelfLifeByWineFromJSON(r.shelfLifeDaysByWine);
  if (Object.keys(byWine).length > 0) out.shelfLifeDaysByWine = byWine;
  return out;
}

/**
 * A bottle this small is ONE serving: a 187 ml split is poured whole, never as a
 * 6 oz "glass" that leaves a third of an ounce in the bottle (House of Pho,
 * 2026-10-14). Such a wine is sold by the bottle only. A 375 ml half bottle
 * still pours glasses. Fixed by SIZE, not by the pour, so changing the glass
 * pour never adds or archives a wine's servings.
 */
export const BAR_WINE_SINGLE_SERVE_MAX_ML = 250;

export function barWineIsSingleServe(sizeMl: number | null | undefined): boolean {
  return typeof sizeMl === "number" && sizeMl > 0 && sizeMl <= BAR_WINE_SINGLE_SERVE_MAX_ML;
}

/**
 * The servings a wine is offered in at this location, in till order. A
 * single-serve bottle (`barWineIsSingleServe`) is the bottle alone.
 */
export function barWineServingsOffered(
  settings: IBarWineSettings,
  opts: { readonly singleServe?: boolean } = {},
): BarWineServing[] {
  if (opts.singleServe) return ["bottle"];
  return BAR_WINE_SERVINGS.filter((s) => s !== "large" || settings.largePourOz !== null);
}

/**
 * Millilitres in one bottle of this carried wine: the item's own size (a
 * carry at another size records it), else the catalog row's, else worked back
 * from its ounces. `null` when nothing says.
 */
export function barWineBottleMl(
  item: { readonly bottleSizeMl?: number | null; readonly containerSize?: number | null },
  catalogSizeMl?: number | null,
): number | null {
  if (typeof item.bottleSizeMl === "number" && item.bottleSizeMl > 0) return item.bottleSizeMl;
  if (typeof catalogSizeMl === "number" && catalogSizeMl > 0) return catalogSizeMl;
  if (typeof item.containerSize === "number" && item.containerSize > 0) {
    return Math.round(item.containerSize * ML_PER_STOCK_UNIT.oz);
  }
  return null;
}

/**
 * What a generated wine item is called on the till: brand and product, plus the
 * size when it is not a standard 750 ml bottle — so a 750 ml and a 187 ml of
 * the same prosecco are two buttons a server can tell apart.
 */
export function barWineItemName(name: string, sizeMl: number | null | undefined): string {
  if (typeof sizeMl !== "number" || !(sizeMl > 0) || Math.round(sizeMl) === 750) return name;
  return `${name} ${formatBarBottleSize(sizeMl)}`;
}

/**
 * Ounces in one bottle of this carried wine. The inventory item's
 * `containerSize` is authoritative (it is what a count divides by); a row
 * without one falls back to its bottle size. `null` when neither is known —
 * a bottle serving then cannot draw, and the service refuses to list it.
 */
export function barWineBottleOz(item: {
  readonly containerSize?: number | null;
  readonly bottleSizeMl?: number | null;
}): number | null {
  if (typeof item.containerSize === "number" && item.containerSize > 0) return item.containerSize;
  if (typeof item.bottleSizeMl === "number" && item.bottleSizeMl > 0) {
    return Number((item.bottleSizeMl / ML_PER_STOCK_UNIT.oz).toFixed(2));
  }
  return null;
}

/**
 * What one serving takes from stock, in ounces: a glass the glass pour, a large
 * glass the large pour, a bottle the WHOLE bottle. `null` for a serving the
 * location does not offer.
 */
export function barWineServingOz(
  serving: BarWineServing,
  settings: IBarWineSettings,
  bottleOz: number,
): number | null {
  if (serving === "glass") return settings.glassOz;
  if (serving === "large") return settings.largePourOz;
  return bottleOz;
}

/** The till's description of a serving: "5 oz pour", "Full bottle (25.36 oz)". */
export function barWineServingPosDescription(serving: BarWineServing, oz: number): string {
  return serving === "bottle" ? `Full bottle (${formatOz(oz)} oz)` : `${formatOz(oz)} oz pour`;
}

/**
 * Glasses in one bottle at a pour, and what is left over: 25.36 oz at 5 oz is
 * 5 glasses with 0.36 oz left. Information for the person choosing a pour —
 * OrderUp never suggests a price from it.
 */
export function barWinePoursPerBottle(bottleOz: number, pourOz: number): { pours: number; leftoverOz: number } {
  if (!(bottleOz > 0) || !(pourOz > 0)) return { pours: 0, leftoverOz: 0 };
  const pours = Math.floor((bottleOz + 1e-9) / pourOz);
  return { pours, leftoverOz: Number((bottleOz - pours * pourOz).toFixed(2)) };
}

function formatOz(oz: number): string {
  return Number(oz.toFixed(2)).toString();
}

// ── A glass sold as a split ──────────────────────────────────────────────

/**
 * A wine's GLASS can be sold as a whole single-serve bottle of another carried
 * wine (House of Pho, 2026-10-15): Poggio Costa Prosecco is poured by the glass
 * as a 187 ml split and sold by the bottle as the 750 ml — ONE till line whose
 * Serving choice is Glass or Bottle, each at its own price. The link lives on
 * the 750 ml's inventory item (`barWineGlassFrom`). Its Glass then takes one
 * whole split off stock, prints as a split on the bar ticket, and opens no
 * bottle (its option is tagged as the split's BOTTLE serving, which the till
 * and the API both skip for open-bottle tracking). The split's own line comes
 * off the till while it is another wine's Glass.
 */
export interface BarWineGlassFromWine {
  readonly inventoryGuid: string;
  readonly name: string;
  readonly sizeMl: number | null;
  readonly bottleOz: number | null;
  /** `barWineGlassFrom` as stored on the item. */
  readonly glassFrom?: string | null;
}

/**
 * The split a wine's Glass is sold as, when its stored link still holds: the
 * split is carried, single-serve and sized, and the wine itself is not a split.
 * `null` otherwise — a glass is then poured from the wine's own bottle, so a
 * split that stops being carried can never leave a glass drawing nothing.
 */
export function barWineGlassSource<T extends BarWineGlassFromWine>(wine: T, carried: ReadonlyMap<string, T>): T | null {
  const from = wine.glassFrom;
  if (!from || from === wine.inventoryGuid || barWineIsSingleServe(wine.sizeMl)) return null;
  const split = carried.get(from);
  if (!split || !barWineIsSingleServe(split.sizeMl) || split.bottleOz === null) return null;
  return split;
}

/**
 * Whether `split` may be sold as the Glass of `wine` — or, with `split: null`,
 * whether the wine's Glass may go back to a pour from its bottle (always). A
 * refusal is in the manager's words.
 */
export function planBarWineGlassFrom<T extends BarWineGlassFromWine>(input: {
  readonly wine: T | undefined;
  /** `null` = pour the glass from the wine's own bottle. */
  readonly splitGuid: string | null;
  readonly carried: ReadonlyMap<string, T>;
}): { readonly ok: true; readonly split: T | null } | { readonly ok: false; readonly message: string } {
  const { wine, splitGuid, carried } = input;
  if (!wine) return { ok: false, message: "That wine is not carried here any more — reload the page." };
  const wineName = barWineItemName(wine.name, wine.sizeMl);
  if (barWineIsSingleServe(wine.sizeMl)) {
    return { ok: false, message: `"${wineName}" is a single serving itself — it is sold by the bottle.` };
  }
  if (splitGuid === null) return { ok: true, split: null };
  const split = carried.get(splitGuid);
  if (!split || split.inventoryGuid === wine.inventoryGuid) {
    return { ok: false, message: "That split is not a wine carried here — reload the page." };
  }
  const splitName = barWineItemName(split.name, split.sizeMl);
  if (!barWineIsSingleServe(split.sizeMl)) {
    return {
      ok: false,
      message: `"${splitName}" is not a single serving — a glass can be sold as a bottle of ${BAR_WINE_SINGLE_SERVE_MAX_ML} ml or less.`,
    };
  }
  if (split.bottleOz === null) {
    return { ok: false, message: `"${splitName}" has no bottle size, so a glass could not take it from stock.` };
  }
  for (const other of carried.values()) {
    if (other.inventoryGuid !== wine.inventoryGuid && barWineGlassSource(other, carried)?.inventoryGuid === split.inventoryGuid) {
      return {
        ok: false,
        message: `"${splitName}" is already the Glass of "${barWineItemName(other.name, other.sizeMl)}". A split is the Glass of one wine.`,
      };
    }
  }
  return { ok: true, split };
}

/** The till description of a Glass sold as a split: "187 ml split — the whole bottle". */
export function barWineSplitGlassPosDescription(sizeMl: number | null): string {
  return `${typeof sizeMl === "number" && sizeMl > 0 ? formatBarBottleSize(sizeMl) : "Single-serve"} split — the whole bottle`;
}

/** What the bar ticket prints for a Glass sold as a split, so nobody pours it from the big bottle: "Glass (187 ml split)". */
export function barWineSplitGlassChitName(sizeMl: number | null): string {
  return `Glass (${typeof sizeMl === "number" && sizeMl > 0 ? formatBarBottleSize(sizeMl) : "single-serve"} split)`;
}

/** Text OrderUp wrote for a split glass — anything else on the option was typed by the restaurant and is kept. */
export const isBarWineSplitGlassChitName = (v: unknown): boolean => typeof v === "string" && /^Glass \(.+ split\)$/.test(v);
export const isBarWineSplitGlassPosDescription = (v: unknown): boolean =>
  typeof v === "string" && / split — the whole bottle$/.test(v);

// ── Prices ───────────────────────────────────────────────────────────────

/** The largest serving price the page accepts — a typo guard, not a policy. */
export const BAR_WINE_PRICE_MAX = 10000;

/** A price the save accepts: whole cents in [0, 10000]. */
export function isValidBarWinePrice(price: unknown): price is number {
  return (
    typeof price === "number" &&
    Number.isFinite(price) &&
    price >= 0 &&
    price <= BAR_WINE_PRICE_MAX &&
    Math.abs(price * 100 - Math.round(price * 100)) < 1e-6
  );
}

// ── Document ids ─────────────────────────────────────────────────────────

/** The generated "Wine" menu group the listed wines are placed in. */
export const BAR_WINE_LIST_MENU_GROUP_DOC_ID = "bar_wine_list";
export const BAR_WINE_LIST_MENU_GROUP_NAME = "Wine";
/** The till name of the Serving group ("SERVING"), and the old per-wine groups' name suffix. */
export const BAR_WINE_SERVING_GROUP_NAME = "Serving";

/**
 * The ONE Serving group every listed wine shares (owner 2026-10-16: "i hate
 * duplicates"; design `2026-10-16-wine-shared-serving-group-design.md`): one
 * Glass, one Large Glass, one Bottle for the whole list. It is
 * `pricedByItem` — each wine prices the servings it sells on its OWN link to
 * the group, and a serving a wine does not price is not offered on it.
 */
export const BAR_WINE_SERVING_GROUP_DOC_ID = "bar_wine_serving";
export const BAR_WINE_SHARED_SERVING_GROUP_NAME = "Wine Serving";
export const barWineServingOptionDocId = (serving: BarWineServing): string => `${BAR_WINE_SERVING_GROUP_DOC_ID}_${serving}`;

export const barWineItemDocId = (inventoryGuid: string): string => `bar_wine_${inventoryGuid}`;
/** LEGACY (before 2026-10-16): a wine's own Serving group — read only to upgrade a location. */
export const barWineGroupDocId = (inventoryGuid: string): string => `bar_wine_${inventoryGuid}`;
/** LEGACY (before 2026-10-16): a wine's own serving option — past orders and upgrades name it. */
export const barWineOptionDocId = (inventoryGuid: string, serving: BarWineServing): string =>
  `bar_wine_${inventoryGuid}_${serving}`;

/**
 * `IModifier.barWine` — the serving option's OWN record of what it pours,
 * written by the generator beside the option's other fields. It is what lets a
 * till recognise a wine glass from its CACHED menu (a cached option has no
 * document id) and work the open bottle out itself when the API cannot answer
 * (offline). The document id stays the fallback for an option written before it.
 */
export interface IBarWineOptionTag {
  readonly inventoryGuid: string;
  readonly serving: BarWineServing;
  /** Ounces in one bottle of the wine, as listed — a NEW bottle is this size. */
  readonly bottleOz: number;
  readonly style: BarWineStyle;
}

export function barWineOptionTagFromJSON(raw: unknown): IBarWineOptionTag | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.inventoryGuid !== "string" || r.inventoryGuid.length === 0) return null;
  if (!(BAR_WINE_SERVINGS as readonly unknown[]).includes(r.serving)) return null;
  if (typeof r.bottleOz !== "number" || !(r.bottleOz > 0)) return null;
  const style = (BAR_WINE_STYLES as readonly unknown[]).includes(r.style) ? (r.style as BarWineStyle) : "other";
  return { inventoryGuid: r.inventoryGuid, serving: r.serving as BarWineServing, bottleOz: r.bottleOz, style };
}

export const barWineOptionTagsEqual = (a: IBarWineOptionTag | null, b: IBarWineOptionTag | null): boolean =>
  a === b ||
  (!!a && !!b && a.inventoryGuid === b.inventoryGuid && a.serving === b.serving && a.bottleOz === b.bottleOz && a.style === b.style);

/** A SHARED option's `barWine` tag (`bar_wine_serving_{serving}`): the serving only — the wine is the item's. */
export interface IBarWineServingOptionTag {
  readonly serving: BarWineServing;
}

/** `IModifier.barWine` as stored: a per-wine option's full tag, or a shared option's serving. */
export type BarWineAnyOptionTag = IBarWineOptionTag | IBarWineServingOptionTag;

export function barWineAnyOptionTagFromJSON(raw: unknown): BarWineAnyOptionTag | null {
  const full = barWineOptionTagFromJSON(raw);
  if (full) return full;
  const serving = barWineServingOfOptionTag(raw);
  return serving ? { serving } : null;
}

/** The serving a shared option pours, from its `barWine` tag (`{ serving }`, or a legacy tag that also names its wine). */
export function barWineServingOfOptionTag(raw: unknown): BarWineServing | null {
  if (!raw || typeof raw !== "object") return null;
  const serving = (raw as Record<string, unknown>).serving;
  return (BAR_WINE_SERVINGS as readonly unknown[]).includes(serving) ? (serving as BarWineServing) : null;
}

/** One bottle a serving pours from: which wine, how big a new one is, how long an open one keeps. */
export interface IBarWineBottleTag {
  readonly inventoryGuid: string;
  readonly bottleOz: number;
  readonly style: BarWineStyle;
}

/**
 * `IItem.barWine` — what a listed wine's ITEM pours, now that its servings are
 * the shared options (design 2026-10-16): its own bottle, and the split a Glass
 * of it is sold as, when it is one (`barWineGlassFrom`). A till reads it from
 * its cached menu to recognise a glass and, offline, to open a new bottle of
 * the right size; the API reads it on every send.
 */
export interface IBarWineItemTag extends IBarWineBottleTag {
  readonly glassFrom?: IBarWineBottleTag;
}

function bottleTagFromJSON(raw: unknown): IBarWineBottleTag | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.inventoryGuid !== "string" || r.inventoryGuid.length === 0) return null;
  if (typeof r.bottleOz !== "number" || !(r.bottleOz > 0)) return null;
  const style = (BAR_WINE_STYLES as readonly unknown[]).includes(r.style) ? (r.style as BarWineStyle) : "other";
  return { inventoryGuid: r.inventoryGuid, bottleOz: r.bottleOz, style };
}

export function barWineItemTagFromJSON(raw: unknown): IBarWineItemTag | null {
  const own = bottleTagFromJSON(raw);
  if (!own) return null;
  const glassFrom = bottleTagFromJSON((raw as Record<string, unknown>).glassFrom);
  return glassFrom ? { ...own, glassFrom } : own;
}

const bottleTagsEqual = (a: IBarWineBottleTag | null | undefined, b: IBarWineBottleTag | null | undefined): boolean =>
  (a ?? null) === (b ?? null) ||
  (!!a && !!b && a.inventoryGuid === b.inventoryGuid && a.bottleOz === b.bottleOz && a.style === b.style);

export const barWineItemTagsEqual = (a: IBarWineItemTag | null, b: IBarWineItemTag | null): boolean =>
  a === b || (!!a && !!b && bottleTagsEqual(a, b) && bottleTagsEqual(a.glassFrom, b.glassFrom));

/**
 * What a sent serving POURS — the full tag the open-bottle rules read — from
 * the option's tag and the line's item's tag:
 *   - an option tag that names its own wine (a per-wine option from before
 *     2026-10-16: an order rung then, a till's cached menu) is read as it is;
 *   - a shared option (`{ serving }`) pours from the item's wine;
 *   - a Glass of a wine sold as a split is the SPLIT's bottle serving — the
 *     whole split, so no 750 ml is opened for it.
 * `null` when it is not a wine serving. Pinned by `barOpenBottle.cases.json`
 * (`servingTag`), which the till's port runs too.
 */
export function barWineServingTag(itemTag: unknown, optionTag: unknown): IBarWineOptionTag | null {
  const legacy = barWineOptionTagFromJSON(optionTag);
  if (legacy) return legacy;
  const serving = barWineServingOfOptionTag(optionTag);
  const item = barWineItemTagFromJSON(itemTag);
  if (!serving || !item) return null;
  if (serving === "glass" && item.glassFrom) return { ...item.glassFrom, serving: "bottle" };
  return { inventoryGuid: item.inventoryGuid, serving, bottleOz: item.bottleOz, style: item.style };
}

// ── Till order ───────────────────────────────────────────────────────────

/**
 * The "Wine" group's items, put in the order the manager dragged the wines into
 * (Bar → Wine list → Order on the till). The till lays a group's buttons out by
 * `items[].position`, so this IS the till order.
 *
 * Only the wine slots move: an item in the group that is not a listed wine (one
 * the restaurant put there, or an archived wine) keeps its place, and the wines
 * fill the remaining slots in the order given. Positions are renumbered 0..n-1.
 * `order` must name every listed wine in the group exactly once, or it is
 * `stale` — a wine added or removed since the page loaded.
 */
export function planWineGroupOrder<T extends { readonly itemId: number; readonly position?: number }>(
  items: readonly T[],
  wineByItemId: ReadonlyMap<number, string>,
  order: readonly string[],
): { readonly stale: true } | { readonly stale: false; readonly items: T[]; readonly changed: boolean } {
  // One rule for every "drag into till order" on the Bar page (the spirit lists use it too).
  const plan = reorderOwnSlots(items, (ref) => wineByItemId.get(ref.itemId), order);
  return plan.stale ? plan : { stale: false, items: plan.refs, changed: plan.changed };
}

/** The listed wines' inventory guids in till order (`items[].position`), for the page's drag list. */
export function wineTillOrder(
  items: ReadonlyArray<{ readonly itemId: number; readonly position?: number }>,
  wineByItemId: ReadonlyMap<number, string>,
): string[] {
  return [...items]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .flatMap((ref) => {
      const guid = wineByItemId.get(ref.itemId);
      return guid === undefined ? [] : [guid];
    });
}

// ── Which carried items are wine ─────────────────────────────────────────

/**
 * A carried inventory item that can go on the wine list: a catalog WINE row,
 * an own bottle whose Kind is wine (`barBottleKind`), or an older own bottle
 * filed under a WINE role — stocked by the ounce (the stock rule every carried
 * wine follows), so a serving's draw shares its unit. The catalog row's
 * category outranks the own kind, which outranks the role: a role is a
 * house-pour claim, and a wine should never need one to be a wine.
 */
export function isBarWineCarrier(input: {
  readonly catalogCategory?: string | null;
  readonly ownKind?: string | null;
  readonly roleCategory?: string | null;
  readonly unit?: string | null;
}): boolean {
  if (input.unit !== "oz") return false;
  if (input.catalogCategory) return input.catalogCategory === "wine";
  if (input.ownKind) return input.ownKind === "wine";
  return input.roleCategory === "WINE";
}

// ── Open-bottle tracking (Layer 2) ───────────────────────────────────────

/**
 * The open bottle of one wine, as the bar ticket reasons about it:
 * the location's `bar_open_bottles/{barOpenBottleDocId(...)}` — one per wine,
 * or one per wine PER BAR where glasses route to more than one bar station
 * (a main bar and a service bar each have their own open bottle). Advanced when
 * a GLASS is sent to the bar (stock itself moves at order close, so on-hand
 * cannot say whether a bottle is open). A bottle sale takes a SEALED bottle
 * and never touches it. Server-written only.
 */
export interface IBarOpenBottle {
  readonly inventoryGuid: string;
  /** When the bottle being poured was opened. */
  readonly openedAt: Date;
  /** Ounces poured from it so far. */
  readonly pouredOz: number;
  /** Ounces in one bottle, captured when it was opened. */
  readonly bottleOz: number;
  readonly style: BarWineStyle;
  /** The bar station this bottle is open at; absent/`null` = the location's one shared bottle. */
  readonly stationId?: number | null;
}

/** What the bar ticket says about one sent glass line. Ordered as they happen. */
export type BarOpenBottleNote =
  | { readonly kind: "open_new" }
  | { readonly kind: "finish_and_open"; readonly finishOz: number }
  | { readonly kind: "last_glass" }
  | { readonly kind: "aged"; readonly days: number };

/** The ticket wording, upper-case so it stands out on a chit and a KDS card. */
export function barOpenBottleNoteText(note: BarOpenBottleNote): string {
  switch (note.kind) {
    case "open_new":
      return "OPEN NEW BOTTLE";
    case "finish_and_open":
      return `FINISH BOTTLE (${formatOz(note.finishOz)} OZ) + OPEN NEW`;
    case "last_glass":
      return "LAST GLASS IN BOTTLE";
    case "aged":
      return `OPEN ${note.days} DAYS — CHECK BEFORE POURING`;
  }
}

/** Below this an open bottle counts as empty — a pour is never measured finer. */
const EMPTY_OZ = 0.05;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Pour `glasses` glasses of `pourOz` from the open bottle, one after another.
 *
 *  - No open bottle, or an empty one → OPEN NEW BOTTLE.
 *  - Less left than a glass → FINISH BOTTLE (x OZ) + OPEN NEW: the glass is
 *    topped up from a fresh bottle, which is then the open one.
 *  - A glass that leaves less than one more glass → LAST GLASS IN BOTTLE.
 *  - The bottle poured from was opened before its shelf life ran out → OPEN N
 *    DAYS — CHECK BEFORE POURING (said once, before the first glass).
 *
 * Pure: the caller stores `next` and the notes in ONE transaction, once per
 * sent line.
 */
export function planOpenBottlePours(input: {
  readonly current: IBarOpenBottle | null;
  readonly inventoryGuid: string;
  readonly bottleOz: number;
  readonly style: BarWineStyle;
  readonly pourOz: number;
  readonly glasses: number;
  readonly now: Date;
  readonly shelfLifeDays: number;
  /** The bar the glasses pour at (see `barWinePourStationId`); a new bottle is opened there. */
  readonly stationId?: number | null;
}): { readonly next: IBarOpenBottle; readonly notes: readonly BarOpenBottleNote[] } {
  const notes: BarOpenBottleNote[] = [];
  const add = (n: BarOpenBottleNote) => {
    if (!notes.some((x) => JSON.stringify(x) === JSON.stringify(n))) notes.push(n);
  };
  let bottle: IBarOpenBottle | null = input.current;
  const fresh = (): IBarOpenBottle => ({
    inventoryGuid: input.inventoryGuid,
    openedAt: input.now,
    pouredOz: 0,
    bottleOz: input.bottleOz,
    style: input.style,
    ...(input.stationId != null ? { stationId: input.stationId } : {}),
  });
  const remaining = (b: IBarOpenBottle) => Number((b.bottleOz - b.pouredOz).toFixed(2));

  if (bottle && remaining(bottle) > EMPTY_OZ) {
    const days = Math.floor((input.now.getTime() - new Date(bottle.openedAt).getTime()) / DAY_MS);
    if (days >= input.shelfLifeDays) add({ kind: "aged", days });
  }
  const glasses = Math.max(1, Math.floor(input.glasses));
  for (let i = 0; i < glasses; i += 1) {
    if (!bottle || remaining(bottle) <= EMPTY_OZ) {
      add({ kind: "open_new" });
      bottle = fresh();
    }
    const left = remaining(bottle);
    if (left + EMPTY_OZ < input.pourOz) {
      add({ kind: "finish_and_open", finishOz: left });
      bottle = { ...fresh(), pouredOz: Number((input.pourOz - left).toFixed(2)) };
    } else {
      bottle = { ...bottle, pouredOz: Number((bottle.pouredOz + input.pourOz).toFixed(2)) };
    }
    if (remaining(bottle) + EMPTY_OZ < input.pourOz) add({ kind: "last_glass" });
  }
  return { next: bottle!, notes };
}

/**
 * The open-bottle document id: the wine's inventory guid for the shared
 * bottle, `{inventoryGuid}__station_{prepStationId}` for one bar's own.
 * And the per-line marker id (one marker per sent order line).
 */
export const barOpenBottleDocId = (inventoryGuid: string, stationId?: number | null): string =>
  stationId == null ? inventoryGuid : `${inventoryGuid}__station_${stationId}`;
export const barOpenBottlePourDocId = (orderItemGuid: string): string => orderItemGuid;

/** An open-bottle document id back to its wine and bar; `null` for anything malformed. */
export function parseBarOpenBottleDocId(docId: string): { inventoryGuid: string; stationId: number | null } | null {
  const m = /^(.+)__station_(\d+)$/.exec(docId);
  if (m) return { inventoryGuid: m[1]!, stationId: Number(m[2]) };
  if (!docId || docId.includes("/") || docId.includes("__station_")) return null;
  return { inventoryGuid: docId, stationId: null };
}

/**
 * Which bar a sent glass pours at, from the prep stations its line routes to:
 * the ONE bar-type station among them. None, or more than one (a glass that
 * prints at two bars, so either could pour it), is the shared bottle (`null`)
 * — a guess would split one physical bottle into two records.
 */
export function barWinePourStationId(
  routedStationIds: readonly number[],
  stations: ReadonlyArray<{ readonly prepStationId?: number | null; readonly stationType?: string | null }>,
): number | null {
  const bars = new Set<number>();
  for (const id of routedStationIds) {
    const station = stations.find((s) => s.prepStationId === id);
    if (station?.stationType === "bar") bars.add(id);
  }
  return bars.size === 1 ? [...bars][0]! : null;
}

/**
 * The open bottle after someone at the bar says how much is LEFT in it (a
 * spilled glass, a glass rung and never poured, a pour nobody rang). `null` =
 * empty, the bottle is finished. Opened-at is kept; no stock moves — stock is
 * the order close and the count.
 */
export function barOpenBottleWithRemaining(current: IBarOpenBottle, remainingOz: number): IBarOpenBottle | null {
  const left = Number(Math.min(Math.max(0, remainingOz), current.bottleOz).toFixed(2));
  if (left <= EMPTY_OZ) return null;
  return { ...current, pouredOz: Number((current.bottleOz - left).toFixed(2)) };
}

/** A serving option's document id back to its wine and serving, or `null` for any other modifier. */
export function parseBarWineOptionDocId(docId: string): { inventoryGuid: string; serving: BarWineServing } | null {
  const m = /^bar_wine_(.+)_(glass|large|bottle)$/.exec(docId);
  // The SHARED options (`bar_wine_serving_glass`) name no wine — a wine is
  // never called "serving"; its item's tag says which wine they pour.
  if (!m || m[1] === "serving") return null;
  return { inventoryGuid: m[1]!, serving: m[2] as BarWineServing };
}

/**
 * What a physical COUNT says about the open bottle. A shelf counted at 3.4
 * bottles holds one open bottle with 0.4 of a bottle in it — the count is the
 * truth, so the estimate takes it. `null` = no open bottle (whole bottles, or
 * nothing). The opened-at time is kept: a count says how much is left, not
 * when it was opened.
 */
export function barOpenBottleFromCount(
  current: IBarOpenBottle | null,
  countedOz: number,
): IBarOpenBottle | null {
  if (!current || !(current.bottleOz > 0)) return null;
  const left = Number((Math.max(0, countedOz) % current.bottleOz).toFixed(2));
  if (left <= EMPTY_OZ || current.bottleOz - left <= EMPTY_OZ) return null;
  return { ...current, pouredOz: Number((current.bottleOz - left).toFixed(2)) };
}
